import { Link } from "react-router-dom"
import { personName } from "@/lib/utils"
import type { DraftDetail } from "./types"

/**
 * Vom Portal erzeugte Review-Eintraege (Nachhak, Rueckruf, Telefon-Anliegen).
 *
 * Sie kamen nicht von aussen — ihr „Absender" ist der Kunde, den es BETRIFFT,
 * ihr „Original-Body" ist unsere interne Erklaerung. Beides muss die
 * Oberflaeche sagen, sonst antwortet jemand auf etwas, das nie ankam (#26),
 * oder versteht die Karte gar nicht: „Die Original-Mail gibt es ja so gar
 * nicht" (Paul, 06.09.2026, zu einer Nachhak-Karte an meiti GmbH).
 */

/**
 * 🔴 Neuer proaktiver Ausloeser = HIER eintragen. Ohne Eintrag steht in der
 * Liste nur „Proaktiv". `tests/test_proaktiv_beschriftung.py` gleicht diese
 * Liste gegen das Backend ab.
 */
export function proactiveLabel(trigger: string | null | undefined): string {
  return ({
    reply_reminder: "Nachhak",
    missed_callback: "Rueckruf",
    voice_anliegen: "Telefon",
    // Kommt NICHT aus proactive_drafts, sondern aus services/todo_actions.py
    // (`PROACTIVE_TRIGGER`). Genau deshalb hatte der Waechter ihn bis zum
    // 25.08.2026 nicht gesehen — er las nur die eine Quelle, und die drei
    // Eintraege standen als „Proaktiv" in der Liste.
    pipeline_todo: "Vorgang",
  } as Record<string, string>)[trigger || ""] || "Proaktiv"
}

/** Was das Portal vorschlaegt — als Satz, fuer den Kartenkopf. */
export function proaktivTitel(trigger: string | null | undefined): string {
  return ({
    reply_reminder: "Nachfassen vorgeschlagen",
    missed_callback: "Rueckruf vorgeschlagen",
    voice_anliegen: "Anliegen aus dem KI-Telefon",
    pipeline_todo: "Naechster Schritt im Vorgang",
  } as Record<string, string>)[trigger || ""] || "Vorschlag des Portals"
}

/** Bei Portal-Zeilen steht im Absenderfeld „Nachhak: Name <mail>" — der Kunde,
 *  den es betrifft. Der Praefix ist Technik, nicht Name. */
export function betroffenerName(absender?: string | null, mail?: string | null): string {
  const ohnePrefix = (absender || "").replace(/^\s*[A-Za-zÄÖÜäöü]+:\s*/, "")
  return personName(ohnePrefix || absender, mail)
}

function datumKurz(iso?: string | null): string {
  if (!iso) return ""
  try {
    return new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" })
  } catch { return "" }
}

/**
 * Ersetzt in der Karte den Block „Original-Mail": sagt, WARUM das Portal den
 * Eintrag erzeugt hat, und verweist auf die Mail, um die es geht.
 */
export function ProaktivHerkunft({ detail }: { detail: DraftDetail }) {
  const q = detail.proactive_quelle
  const trigger = detail.proactive_trigger
  return (
    <div style={{
      background: "#EEF2FF", border: "0.5px solid #C7D2FE", borderRadius: 8,
      padding: "10px 12px", fontSize: 12, color: "#2C2C2A", lineHeight: 1.55,
    }}>
      <div style={{ fontWeight: 600, color: "#3C3489", marginBottom: 4 }}>
        Vom Portal erzeugt · {proactiveLabel(trigger)} — keine Mail von aussen
      </div>
      {trigger === "reply_reminder" && q ? (
        <div>
          Unsere Antwort{q.gesendet_am ? ` vom ${datumKurz(q.gesendet_am)}` : ""} an{" "}
          <strong>{personName(q.sender)}</strong>
          {q.subject ? <> auf „{q.subject}"</> : null} blieb bisher ohne Reaktion.
          {" "}Das Portal schlaegt vor, nachzufassen.{" "}
          <Link to={`/emails/${q.email_id}`} style={{ color: "#3C3489" }}>Damalige Mail ansehen</Link>
        </div>
      ) : (
        <div style={{ whiteSpace: "pre-wrap" }}>
          {(detail.original_body || detail.original_excerpt || "").trim()
            || "Das Portal hat diesen Vorschlag aus den Daten abgeleitet."}
        </div>
      )}
    </div>
  )
}
